import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { FreteLocalFields, cidadeUf } from "./FreteLocalFields";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Search, ChevronRight, ChevronLeft, Loader2, Sparkles, User, FileText, FileSignature,
  Package, Wallet, Users, Plus, Trash2, Eye, Check, Mail, MessageCircle, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const fmtBRL = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const STEPS = [
  { n: 1, l: "Cliente", icon: User },
  { n: 2, l: "Modelo Proposta", icon: FileText },
  { n: 3, l: "Modelo Contrato", icon: FileSignature },
  { n: 4, l: "Equipamentos", icon: Package },
  { n: 5, l: "Condição", icon: Wallet },
  { n: 6, l: "Assinaturas", icon: Users },
];

const EMPTY_ITEM = { catalogo_id: null, nome: "", quantidade: 1, valor_mensal: 0, prazo_meses: 12, descricao: "" };

export default function PropostaWizard6({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);

  // Listas
  const [clientes, setClientes] = useState([]);
  const [buscaCliente, setBuscaCliente] = useState("");
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [modelosContrato, setModelosContrato] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [condicoes, setCondicoes] = useState([]);

  // Seleções
  const [clienteSel, setClienteSel] = useState(null);
  const [templateSel, setTemplateSel] = useState(null);
  const [modeloContratoSel, setModeloContratoSel] = useState(null);
  const [itens, setItens] = useState([]);
  const [condicaoSel, setCondicaoSel] = useState(null);
  const [observacoesCondicao, setObservacoesCondicao] = useState("");
  const [validadeDias, setValidadeDias] = useState(15);
  const [frete, setFrete] = useState("");
  const [localOperacao, setLocalOperacao] = useState("");

  // Filtros equipamentos
  const [catFiltro, setCatFiltro] = useState("");

  // Assinaturas + canais
  const [assinaturas, setAssinaturas] = useState({
    cliente:       { nome: "", email: "", whatsapp: "", canal: "email" },
    vendedor:      { nome: "", email: "", whatsapp: "", canal: "email" },
    testemunha_1:  { nome: "", email: "", whatsapp: "", canal: "email" },
    testemunha_2:  { nome: "", email: "", whatsapp: "", canal: "email" },
  });

  // Resultado / preview
  const [propostaCriada, setPropostaCriada] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);

  // Carregar listas quando abrir
  useEffect(() => {
    if (!open) return;
    resetWizard();
    Promise.all([
      api.get("/proposta/templates", { params: { ativo: true } }),
      api.get("/configuracoes/modelos-contrato", { params: { ativo: true } }),
      api.get("/proposta/catalogo", { params: { ativo: true } }),
      api.get("/financeiro/condicoes-pagamento", { params: { ativo: true } }),
    ]).then(([t, mc, cat, cond]) => {
      setTemplates(t.data || []);
      setModelosContrato(mc.data || []);
      setCatalogo(cat.data || []);
      setCondicoes(cond.data || []);
    }).catch(() => toast.error("Falha ao carregar dados do wizard"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Busca de clientes (debounced)
  useEffect(() => {
    if (!open || step !== 1) return;
    const t = setTimeout(() => {
      setLoadingClientes(true);
      api.get("/clientes", { params: { q: buscaCliente || undefined, limit: 30 } })
        .then((r) => setClientes(Array.isArray(r.data) ? r.data : r.data?.items || []))
        .catch(() => setClientes([]))
        .finally(() => setLoadingClientes(false));
    }, 250);
    return () => clearTimeout(t);
  }, [open, step, buscaCliente]);

  // Quando cliente é selecionado, pré-preenche assinatura
  useEffect(() => {
    if (clienteSel) {
      setAssinaturas((a) => ({
        ...a,
        cliente: {
          ...a.cliente,
          nome: clienteSel.nome || a.cliente.nome,
          email: clienteSel.email || a.cliente.email,
          whatsapp: clienteSel.telefone || a.cliente.whatsapp,
        },
      }));
    }
  }, [clienteSel]);

  // Quando user logado disponível
  useEffect(() => {
    if (user) {
      setAssinaturas((a) => ({
        ...a,
        vendedor: {
          ...a.vendedor,
          nome: a.vendedor.nome || user.nome || user.email,
          email: a.vendedor.email || user.email,
        },
      }));
    }
  }, [user]);

  const tipoContratoSel = templateSel?.tipo_contrato || "locacao";

  // Auto-selecionar modelo de contrato padrão do tipo escolhido
  useEffect(() => {
    if (templateSel && !modeloContratoSel) {
      const padrao = modelosContrato.find((m) => m.padrao && m.tipo_contrato === templateSel.tipo_contrato);
      if (padrao) setModeloContratoSel(padrao);
    }
  }, [templateSel, modelosContrato, modeloContratoSel]);

  const categorias = useMemo(() => {
    const set = new Set(catalogo.map((c) => c.categoria).filter(Boolean));
    return ["", ...Array.from(set).sort()];
  }, [catalogo]);

  const equipamentosFiltrados = useMemo(() => {
    if (!catFiltro) return catalogo;
    return catalogo.filter((c) => c.categoria === catFiltro);
  }, [catFiltro, catalogo]);

  const totalGeral = useMemo(() => {
    return itens.reduce((s, it) => {
      const q = Number(it.quantidade || 1);
      const v = Number(it.valor_mensal || 0);
      const p = Number(it.prazo_meses || 12);
      return s + (tipoContratoSel === "venda" ? q * v : q * v * p);
    }, 0);
  }, [itens, tipoContratoSel]);

  function resetWizard() {
    setStep(1);
    setClienteSel(null); setBuscaCliente("");
    setTemplateSel(null); setModeloContratoSel(null);
    setItens([]); setCatFiltro("");
    setCondicaoSel(null); setObservacoesCondicao(""); setValidadeDias(15);
    setAssinaturas({
      cliente:      { nome: "", email: "", whatsapp: "", canal: "email" },
      vendedor:     { nome: "", email: "", whatsapp: "", canal: "email" },
      testemunha_1: { nome: "", email: "", whatsapp: "", canal: "email" },
      testemunha_2: { nome: "", email: "", whatsapp: "", canal: "email" },
    });
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPropostaCriada(null);
  }

  const adicionarItemCatalogo = (eq) => {
    setItens((arr) => [
      ...arr,
      {
        ...EMPTY_ITEM,
        catalogo_id: eq.id,
        nome: eq.nome,
        descricao: eq.descricao_padrao || "",
        valor_mensal: Number(eq.valor_referencia_mensal || 0),
        prazo_meses: tipoContratoSel === "venda" ? 1 : 12,
      },
    ]);
    toast.success(`${eq.nome} adicionado`);
  };

  const removerItem = (idx) => setItens((arr) => arr.filter((_, i) => i !== idx));
  const atualizarItem = (idx, patch) => setItens((arr) => arr.map((it, i) => i === idx ? { ...it, ...patch } : it));

  const canNext = () => {
    if (step === 1) return !!clienteSel;
    if (step === 2) return !!templateSel;
    if (step === 3) return !!modeloContratoSel;
    if (step === 4) return true; // itens opcional
    if (step === 5) return !!condicaoSel && !!frete;
    if (step === 6) return !!(assinaturas.cliente.nome && assinaturas.vendedor.nome);
    return false;
  };

  const gerar = async () => {
    if (!clienteSel) { toast.error("Selecione um cliente"); return; }
    setSaving(true);
    try {
      const payload = {
        cliente_id: clienteSel.id,
        template_id: templateSel?.id || null,
        tipo_contrato: tipoContratoSel,
        modelo_contrato_id: modeloContratoSel?.id || null,
        condicao_pagamento_id: condicaoSel?.id || null,
        condicoes_pagamento: observacoesCondicao || condicaoSel?.descricao || condicaoSel?.nome || null,
        validade_dias: Number(validadeDias) || 15,
        frete: frete || null,
        local_operacao: localOperacao || cidadeUf(clienteSel) || clienteSel?.endereco || null,
        itens: itens.map((it) => ({
          catalogo_id: it.catalogo_id || null,
          nome: it.nome,
          descricao: it.descricao || null,
          quantidade: Number(it.quantidade) || 1,
          valor_mensal: Number(it.valor_mensal) || 0,
          prazo_meses: Number(it.prazo_meses) || 12,
        })),
        descricao_solucao: templateSel?.descricao_solucao || null,
        testemunha_1_nome: assinaturas.testemunha_1.nome || null,
        testemunha_1_email: assinaturas.testemunha_1.email || null,
        testemunha_1_whatsapp: assinaturas.testemunha_1.whatsapp || null,
        testemunha_2_nome: assinaturas.testemunha_2.nome || null,
        testemunha_2_email: assinaturas.testemunha_2.email || null,
        testemunha_2_whatsapp: assinaturas.testemunha_2.whatsapp || null,
      };
      const { data } = await api.post("/proposta-nativa/from-cliente", payload);
      setPropostaCriada(data);
      toast.success(`Proposta ${data.numero} criada!`);
      // Gerar PDF logo após
      setPdfLoading(true);
      await api.post(`/proposta-nativa/${data.id}/gerar-pdf`);
      const blob = await api.get(`/proposta-nativa/${data.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([blob.data], { type: "application/pdf" }));
      setPdfUrl(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar proposta");
    } finally {
      setSaving(false);
      setPdfLoading(false);
    }
  };

  const enviarTodos = async () => {
    if (!propostaCriada) return;
    setEnviando(true);
    try {
      const destinatarios = [];
      const papeis = ["cliente", "vendedor", "testemunha_1", "testemunha_2"];
      for (const p of papeis) {
        const a = assinaturas[p];
        const dest = a.canal === "email" ? a.email : a.whatsapp;
        if (a.nome && dest) {
          destinatarios.push({ papel: p, nome: a.nome, canal: a.canal, destino: dest });
        }
      }
      if (destinatarios.length === 0) { toast.error("Preencha pelo menos 1 destinatário"); return; }
      const { data } = await api.post(`/proposta-nativa/${propostaCriada.id}/enviar-multiplo`, { destinatarios });
      const okQt = (data.resultados || []).filter((r) => r.ok).length;
      toast.success(`Enviado para ${okQt}/${data.total} destinatários`);
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no envio múltiplo");
    } finally {
      setEnviando(false);
    }
  };

  const updateAssin = (papel, field, val) => {
    setAssinaturas((a) => ({ ...a, [papel]: { ...a[papel], [field]: val } }));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetWizard(); onOpenChange(v); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="proposta-wizard-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={18} style={{ color: "#F97316" }} />
            Nova Proposta — Wizard de 6 etapas
          </DialogTitle>
          <DialogDescription>
            Fluxo guiado para montar Proposta + Contrato unificados no mesmo PDF, com envio multi-canal.
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-2" data-testid="wizard6-stepper">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const reached = step >= s.n;
            const current = step === s.n;
            return (
              <React.Fragment key={s.n}>
                <div className="flex flex-col items-center min-w-[60px]">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm ${reached ? "" : "bg-slate-200 text-slate-500"}`}
                    style={reached ? { background: current ? "#F97316" : "#1B3A6B" } : {}}
                    data-testid={`wizard6-step-${s.n}`}
                  >
                    {step > s.n ? <Check size={14} /> : <Icon size={14} />}
                  </div>
                  <div className={`text-[10px] mt-1 text-center ${current ? "font-bold text-[#F97316]" : reached ? "text-[#1B3A6B]" : "text-muted-foreground"}`}>
                    {s.n}. {s.l}
                  </div>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 self-start mt-4 ${step > s.n ? "bg-[#1B3A6B]" : "bg-slate-200"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* STEP 1 — Cliente */}
        {step === 1 && (
          <div className="space-y-3" data-testid="wizard6-step-1-content">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="wizard6-busca-cliente" className="pl-9" placeholder="Buscar por nome, CNPJ/CPF ou e-mail..." value={buscaCliente} onChange={(e) => setBuscaCliente(e.target.value)} autoFocus />
            </div>
            <Card className="max-h-[400px] overflow-y-auto">
              {loadingClientes ? (
                <div className="p-8 text-center text-muted-foreground"><Loader2 className="inline animate-spin" /></div>
              ) : clientes.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  Cliente não cadastrado — <a href="/app/clientes" className="text-blue-700 underline">volte ao cadastro</a> para adicionar.
                </div>
              ) : (
                clientes.map((c) => {
                  const selected = clienteSel?.id === c.id;
                  return (
                    <button
                      key={c.id}
                      onClick={() => setClienteSel(c)}
                      className={`w-full text-left p-3 border-b last:border-b-0 transition flex items-center gap-2 ${selected ? "bg-orange-50" : "hover:bg-slate-50"}`}
                      data-testid={`wizard6-cliente-${c.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{c.nome}</div>
                        <div className="text-xs text-muted-foreground truncate">{c.cnpj_cpf || "—"} · {c.email || "—"} · {c.telefone || "—"}</div>
                      </div>
                      {selected && <Check size={16} className="text-orange-600" />}
                    </button>
                  );
                })
              )}
            </Card>
            {clienteSel && (
              <Card className="p-3 bg-orange-50 border-orange-200">
                <div className="text-xs text-muted-foreground">Cliente selecionado:</div>
                <div className="font-semibold">{clienteSel.nome}</div>
                <div className="text-xs text-muted-foreground">{clienteSel.cnpj_cpf} · {clienteSel.email}</div>
              </Card>
            )}
          </div>
        )}

        {/* STEP 2 — Modelo de Proposta */}
        {step === 2 && (
          <div className="space-y-3" data-testid="wizard6-step-2-content">
            <div className="text-sm text-muted-foreground">Escolha o modelo de proposta:</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {templates.map((t) => {
                const sel = templateSel?.id === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTemplateSel(t)}
                    className={`text-left p-3 rounded border-2 transition ${sel ? "border-orange-400 bg-orange-50" : "border-slate-200 hover:border-blue-300 bg-white"}`}
                    data-testid={`wizard6-template-${t.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-semibold">{t.nome}</div>
                      <Badge variant="outline" className="text-[10px]">{t.tipo_contrato}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground line-clamp-3">{t.descricao_solucao?.slice(0, 200) || "—"}</div>
                    {sel && <div className="text-xs text-orange-700 mt-2 flex items-center gap-1"><Check size={12} /> Selecionado</div>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 3 — Modelo de Contrato */}
        {step === 3 && (
          <div className="space-y-3" data-testid="wizard6-step-3-content">
            <div className="text-sm text-muted-foreground">Modelo de contrato (Parte 2 do PDF — depois das assinaturas da proposta):</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {modelosContrato
                .filter((m) => !tipoContratoSel || m.tipo_contrato === tipoContratoSel || m.tipo_contrato === "outros")
                .map((m) => {
                  const sel = modeloContratoSel?.id === m.id;
                  return (
                    <button
                      key={m.id}
                      onClick={() => setModeloContratoSel(m)}
                      className={`text-left p-3 rounded border-2 transition ${sel ? "border-orange-400 bg-orange-50" : "border-slate-200 hover:border-blue-300 bg-white"}`}
                      data-testid={`wizard6-modelo-${m.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-semibold">{m.nome}{m.padrao ? " ⭐" : ""}</div>
                        <Badge variant="outline" className="text-[10px]">{m.tipo_contrato}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-2">{m.observacoes || "—"}</div>
                      {sel && <div className="text-xs text-orange-700 mt-2 flex items-center gap-1"><Check size={12} /> Selecionado</div>}
                    </button>
                  );
                })}
            </div>
          </div>
        )}

        {/* STEP 4 — Equipamentos */}
        {step === 4 && (
          <div className="space-y-3" data-testid="wizard6-step-4-content">
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-xs">Categoria:</Label>
              {categorias.map((cat) => (
                <button
                  key={cat || "all"}
                  onClick={() => setCatFiltro(cat)}
                  className={`px-3 py-1 rounded-full text-xs ${catFiltro === cat ? "text-white" : "bg-slate-100 text-slate-700"}`}
                  style={catFiltro === cat ? { background: "#1B3A6B" } : {}}
                  data-testid={`wizard6-cat-${cat || "all"}`}
                >
                  {cat || "Todas"}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
              {equipamentosFiltrados.map((eq) => (
                <div key={eq.id} className="p-2 border rounded flex items-center justify-between gap-2 bg-white" data-testid={`wizard6-equip-${eq.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{eq.nome}</div>
                    <div className="text-xs text-muted-foreground">{eq.categoria} · {fmtBRL(eq.valor_referencia_mensal)}/mês</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => adicionarItemCatalogo(eq)}><Plus size={12} /></Button>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-muted-foreground italic">
              Equipamentos vinculados ficam disponíveis para qualquer cliente. O estoque real (3.075 equipamentos) é exibido como informativo no detalhe da proposta após criação.
            </div>
            {itens.length > 0 && (
              <Card className="p-3 bg-slate-50">
                <Label className="text-xs font-bold mb-2">Itens adicionados ({itens.length}):</Label>
                <div className="space-y-2">
                  {itens.map((it, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white p-2 rounded border">
                      <div className="col-span-5">
                        <Label className="text-[10px]">Nome</Label>
                        <Input value={it.nome} onChange={(e) => atualizarItem(idx, { nome: e.target.value })} data-testid={`w6-item-${idx}-nome`} className="h-8 text-xs" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px]">Qtd</Label>
                        <Input type="number" min={1} value={it.quantidade} onChange={(e) => atualizarItem(idx, { quantidade: e.target.value })} data-testid={`w6-item-${idx}-qtd`} className="h-8 text-xs" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px]">{tipoContratoSel === "venda" ? "Valor" : "R$/mês"}</Label>
                        <Input type="number" step="0.01" value={it.valor_mensal} onChange={(e) => atualizarItem(idx, { valor_mensal: e.target.value })} data-testid={`w6-item-${idx}-valor`} className="h-8 text-xs" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px]">Prazo (m)</Label>
                        <Input type="number" min={1} value={it.prazo_meses} onChange={(e) => atualizarItem(idx, { prazo_meses: e.target.value })} data-testid={`w6-item-${idx}-prazo`} className="h-8 text-xs" />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button size="sm" variant="ghost" onClick={() => removerItem(idx)} data-testid={`w6-item-${idx}-del`}><Trash2 size={12} className="text-red-600" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-right mt-2 text-sm">Total: <strong style={{ color: "#F97316" }}>{fmtBRL(totalGeral)}</strong></div>
              </Card>
            )}
          </div>
        )}

        {/* STEP 5 — Condição */}
        {step === 5 && (
          <div className="space-y-3" data-testid="wizard6-step-5-content">
            <div className="text-sm text-muted-foreground">Escolha a condição de pagamento:</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {condicoes.map((c) => {
                const sel = condicaoSel?.id === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setCondicaoSel(c)}
                    className={`text-left p-3 rounded border-2 transition ${sel ? "border-orange-400 bg-orange-50" : "border-slate-200 hover:border-blue-300 bg-white"}`}
                    data-testid={`wizard6-cond-${c.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-semibold">{c.nome}</div>
                      <Badge variant="outline" className="text-[10px]">{c.tipo}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">{c.descricao}</div>
                    <div className="text-[11px] mt-1 flex gap-3 text-muted-foreground">
                      {c.parcelas > 1 && <span>{c.parcelas}x</span>}
                      {c.entrada_percentual > 0 && <span>Entrada {c.entrada_percentual}%</span>}
                      {c.prazo_dias > 0 && <span>{c.prazo_dias}d</span>}
                    </div>
                    {sel && <div className="text-xs text-orange-700 mt-1 flex items-center gap-1"><Check size={12} /> Selecionado</div>}
                  </button>
                );
              })}
            </div>
            <div>
              <Label>Observações de pagamento (opcional)</Label>
              <Textarea data-testid="wizard6-cond-obs" rows={2} value={observacoesCondicao} onChange={(e) => setObservacoesCondicao(e.target.value)} placeholder="Notas específicas (ex: entrada via PIX, demais via boleto...)" />
            </div>
            <FreteLocalFields
              frete={frete}
              local={localOperacao || cidadeUf(clienteSel) || clienteSel?.endereco || ""}
              onFrete={setFrete}
              onLocal={setLocalOperacao}
              prefix="wizard6"
            />
            <div>
              <Label>Validade da proposta (dias)</Label>
              <Input type="number" min={1} max={90} value={validadeDias} onChange={(e) => setValidadeDias(e.target.value)} className="max-w-[120px]" data-testid="wizard6-validade" />
            </div>
          </div>
        )}

        {/* STEP 6 — Assinaturas */}
        {step === 6 && !propostaCriada && (
          <div className="space-y-3" data-testid="wizard6-step-6-content">
            <div className="text-sm text-muted-foreground">Defina quem assina e por qual canal cada um recebe o documento:</div>
            {["cliente", "vendedor", "testemunha_1", "testemunha_2"].map((papel) => {
              const labels = { cliente: "Locatário / Cliente", vendedor: "Locador / Vendedor", testemunha_1: "Testemunha 1", testemunha_2: "Testemunha 2" };
              const a = assinaturas[papel];
              return (
                <Card key={papel} className="p-3" data-testid={`wizard6-assin-${papel}`}>
                  <div className="text-xs font-bold uppercase mb-2" style={{ color: "#1B3A6B" }}>{labels[papel]}</div>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-4">
                      <Label className="text-[10px]">Nome</Label>
                      <Input value={a.nome} onChange={(e) => updateAssin(papel, "nome", e.target.value)} className="h-8 text-xs" data-testid={`w6-${papel}-nome`} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px]">E-mail</Label>
                      <Input type="email" value={a.email} onChange={(e) => updateAssin(papel, "email", e.target.value)} className="h-8 text-xs" data-testid={`w6-${papel}-email`} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px]">WhatsApp (E.164)</Label>
                      <Input value={a.whatsapp} onChange={(e) => updateAssin(papel, "whatsapp", e.target.value)} className="h-8 text-xs" placeholder="+55..." data-testid={`w6-${papel}-wpp`} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px]">Canal</Label>
                      <div className="flex gap-1">
                        <button onClick={() => updateAssin(papel, "canal", "email")} className={`flex-1 h-8 rounded text-xs ${a.canal === "email" ? "text-white" : "bg-slate-100"}`} style={a.canal === "email" ? { background: "#1B3A6B" } : {}} data-testid={`w6-${papel}-canal-email`}>
                          <Mail size={10} className="inline mr-1" /> E-mail
                        </button>
                        <button onClick={() => updateAssin(papel, "canal", "whatsapp")} className={`flex-1 h-8 rounded text-xs ${a.canal === "whatsapp" ? "text-white" : "bg-slate-100"}`} style={a.canal === "whatsapp" ? { background: "#16a34a" } : {}} data-testid={`w6-${papel}-canal-wpp`}>
                          <MessageCircle size={10} className="inline mr-1" /> WPP
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
            <div className="text-[11px] text-muted-foreground italic">💡 Cliente e Vendedor são preenchidos automaticamente. Testemunhas são opcionais (preencha se quiser que recebam o PDF).</div>
          </div>
        )}

        {/* STEP 6 — após criação: preview + botão enviar */}
        {step === 6 && propostaCriada && (
          <div className="space-y-3" data-testid="wizard6-step-6-criada">
            <Card className="p-3 bg-emerald-50 border-emerald-200">
              <div className="flex items-center gap-2">
                <Check className="text-emerald-700" />
                <div>
                  <div className="font-bold text-emerald-800">Proposta {propostaCriada.numero} criada com sucesso!</div>
                  <div className="text-xs text-muted-foreground">PDF unificado (Proposta + Contrato) gerado. Pré-visualize abaixo antes de enviar aos 4 destinatários.</div>
                </div>
              </div>
            </Card>
            {pdfLoading && !pdfUrl ? (
              <div className="h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin mr-2" /> Gerando PDF…</div>
            ) : pdfUrl ? (
              <iframe data-testid="wizard6-preview-iframe" src={pdfUrl} title="Preview" className="w-full" style={{ height: "55vh", border: "1px solid #e5e7eb", borderRadius: 6 }} />
            ) : null}
            <Card className="p-3">
              <div className="text-xs font-bold mb-2">Destinatários do envio:</div>
              <div className="space-y-1 text-xs">
                {["cliente", "vendedor", "testemunha_1", "testemunha_2"].map((p) => {
                  const a = assinaturas[p];
                  const dest = a.canal === "email" ? a.email : a.whatsapp;
                  if (!a.nome || !dest) return null;
                  return (
                    <div key={p} className="flex items-center gap-2">
                      {a.canal === "email" ? <Mail size={11} /> : <MessageCircle size={11} className="text-green-600" />}
                      <span><strong>{a.nome}</strong> ({p}) → {dest}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        )}

        {/* Footer navegação */}
        <div className="flex justify-between items-center pt-3 border-t mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving || enviando}>
            <X size={14} className="mr-1" /> Cancelar
          </Button>
          <div className="flex gap-2">
            {step > 1 && !propostaCriada && (
              <Button variant="outline" onClick={() => setStep((s) => s - 1)} disabled={saving} data-testid="wizard6-voltar">
                <ChevronLeft size={14} className="mr-1" /> Voltar
              </Button>
            )}
            {step < 6 && (
              <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext()} className="text-white" style={{ background: "#1B3A6B" }} data-testid="wizard6-proximo">
                Próximo: {STEPS[step]?.l} <ChevronRight size={14} className="ml-1" />
              </Button>
            )}
            {step === 6 && !propostaCriada && (
              <Button onClick={gerar} disabled={saving || !canNext()} className="text-white" style={{ background: "#F97316" }} data-testid="wizard6-gerar">
                {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileText size={14} className="mr-1" />}
                Gerar proposta e contrato
              </Button>
            )}
            {step === 6 && propostaCriada && (
              <Button onClick={enviarTodos} disabled={enviando} className="text-white" style={{ background: "#F97316" }} data-testid="wizard6-enviar-todos">
                {enviando ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Mail size={14} className="mr-1" />}
                Enviar para todos
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
